/* ==========================================================================
   Admin Layout Component
   Task F21: Layout wrapper for admin pages with header, sidebar, and content
   ========================================================================== */

import { useCallback, useEffect } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { Settings, Package, Tags, BadgeDollarSign, Users, Menu, X, LayoutDashboard, UserPlus, CreditCard, BookOpen, FileText, UserCog, ClipboardList, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { LogoutButton } from '@/components/ui/LogoutButton'
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { Fragment } from 'react'
import { useOptionalAdminAuthContext } from '@/context/AdminAuthContext'
import { usePortalSlug } from '@/context/PortalContext'
import { listAccessRequests } from '@/api/admin'
import { PharmacyUserRole } from '@/api/types'
import { WhatsNewSidebarItem } from '@/components/changelog/WhatsNewBadge'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface NavItem {
  id: string
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
  /** Hidden from Staff (PORTAL-94): sections only Admins/Managers can use. */
  managerOnly?: boolean
}

/* --------------------------------------------------------------------------
   Navigation Items
   -------------------------------------------------------------------------- */

function getNavItems(slug: string): NavItem[] {
  return [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: `/rx/${slug}/admin/dashboard`,
      icon: LayoutDashboard,
    },
    {
      id: 'settings',
      label: 'Portal Settings',
      path: `/rx/${slug}/admin/settings`,
      icon: Settings,
      managerOnly: true,
    },
    {
      id: 'catalog',
      label: 'Product Catalog',
      path: `/rx/${slug}/admin/catalog`,
      icon: Package,
    },
    {
      id: 'categories',
      label: 'Categories',
      path: `/rx/${slug}/admin/categories`,
      icon: Tags,
      managerOnly: true,
    },
    {
      id: 'pricing',
      label: 'Pricing',
      path: `/rx/${slug}/admin/pricing`,
      icon: BadgeDollarSign,
    },
    {
      id: 'general-custom-fields',
      label: 'General Custom Fields',
      path: `/rx/${slug}/admin/general-custom-fields`,
      icon: ClipboardList,
      managerOnly: true,
    },
    {
      id: 'providers',
      label: 'Providers',
      path: `/rx/${slug}/admin/providers`,
      icon: Users,
    },
    {
      id: 'access-requests',
      label: 'Access Requests',
      path: `/rx/${slug}/admin/access-requests`,
      icon: UserPlus,
    },
    {
      id: 'faxes',
      label: 'Faxes',
      path: `/rx/${slug}/admin/faxes`,
      icon: FileText,
      managerOnly: true,
    },
    {
      id: 'team',
      label: 'Team',
      path: `/rx/${slug}/admin/team`,
      icon: UserCog,
      managerOnly: true,
    },
    {
      id: 'billing',
      label: 'Billing',
      path: `/rx/${slug}/admin/billing`,
      icon: CreditCard,
      managerOnly: true,
    },
    {
      id: 'guide',
      label: 'User Guide',
      path: `/rx/${slug}/admin/guide`,
      icon: BookOpen,
    },
    {
      id: 'account',
      label: 'My Account',
      path: `/rx/${slug}/admin/account`,
      icon: ShieldCheck,
    },
  ]
}

/* --------------------------------------------------------------------------
   Admin Header Component
   -------------------------------------------------------------------------- */

interface AdminHeaderProps {
  pharmacyName: string
  userName: string
  onLogout: () => void
  onMobileMenuClick: () => void
}

function AdminHeader({
  pharmacyName,
  userName,
  onLogout,
  onMobileMenuClick,
}: AdminHeaderProps) {
  return (
    <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between">
      {/* Left: Logo and pharmacy name */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          type="button"
          onClick={onMobileMenuClick}
          className="lg:hidden p-2 -ml-2 rounded-md text-charcoal hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-lavender"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Logo/Brand */}
        <div className="flex items-center gap-2">
          <Link to="/" className="cursor-pointer">
            <span className="font-serif text-xl text-deep-purple">TJM Labs</span>
          </Link>
          <span className="hidden sm:inline text-gray-300">|</span>
          <span className="hidden sm:inline text-sm text-charcoal/70 truncate max-w-[200px]">
            {pharmacyName}
          </span>
        </div>
      </div>

      {/* Right: User info and logout */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* User name - hidden on mobile */}
        <span className="hidden sm:inline text-sm text-charcoal">
          {userName}
        </span>

        {/* Logout button */}
        <LogoutButton onClick={onLogout} />
      </div>
    </header>
  )
}

/* --------------------------------------------------------------------------
   Desktop Sidebar Component
   -------------------------------------------------------------------------- */

interface SidebarNavProps {
  items: NavItem[]
  currentPath: string
  onNavigate: (path: string) => void
}

function SidebarNav({ items, currentPath, onNavigate }: SidebarNavProps) {
  return (
    <nav className="flex flex-col gap-1 p-4">
      {items.map((item) => {
        // Special handling for dashboard: also match the admin index route
        const isActive =
          item.id === 'dashboard'
            ? currentPath === item.path ||
              currentPath.match(/^\/[^/]+\/admin\/?$/) !== null
            : currentPath.startsWith(item.path)
        const Icon = item.icon

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.path)}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-md text-left w-full
              transition-colors focus:outline-none focus:ring-2 focus:ring-lavender
              ${
                isActive
                  ? 'bg-soft-lavender text-deep-purple font-medium'
                  : 'text-charcoal hover:bg-gray-100'
              }
            `}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon
              className={`h-5 w-5 flex-shrink-0 ${
                isActive ? 'text-deep-purple' : 'text-gray-500'
              }`}
              aria-hidden="true"
            />
            <span className="text-sm">{item.label}</span>
            {item.badge != null && item.badge > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs font-medium rounded-full px-2 py-0.5 min-w-[20px] text-center">
                {item.badge}
              </span>
            )}
          </button>
        )
      })}
    </nav>
  )
}

/* --------------------------------------------------------------------------
   Mobile Sidebar Drawer
   -------------------------------------------------------------------------- */

interface MobileSidebarProps {
  open: boolean
  onClose: () => void
  items: NavItem[]
  currentPath: string
  onNavigate: (path: string) => void
  pharmacyName: string
}

function MobileSidebar({
  open,
  onClose,
  items,
  currentPath,
  onNavigate,
  pharmacyName,
}: MobileSidebarProps) {
  const handleNavigate = (path: string) => {
    onNavigate(path)
    onClose()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50 lg:hidden">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        {/* Sidebar panel */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in duration-150"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <DialogPanel className="fixed inset-y-0 left-0 w-60 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <span className="font-serif text-lg text-deep-purple block">
                  Admin Portal
                </span>
                <span className="text-xs text-charcoal/60 truncate block max-w-[180px]">
                  {pharmacyName}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-md text-gray-500 hover:text-charcoal hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-lavender"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation */}
            <SidebarNav
              items={items}
              currentPath={currentPath}
              onNavigate={handleNavigate}
            />
            <div className="border-t border-gray-200 mx-4" />
            <div className="p-4 pt-2">
              <WhatsNewSidebarItem />
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}

/* --------------------------------------------------------------------------
   Main Admin Layout Component
   -------------------------------------------------------------------------- */

/**
 * Admin layout with header, sidebar, and main content area.
 * Based on design.md specifications:
 * - Header: 64px height with pharmacy name and user menu
 * - Sidebar: 240px width with navigation links
 * - Main content: fluid width with warm-beige background
 *
 * Mobile: Sidebar collapses to hamburger menu in header.
 *
 * @example
 * ```tsx
 * // In App.tsx routing
 * <Route path="admin" element={<AdminLayout />}>
 *   <Route path="settings" element={<Settings />} />
 *   <Route path="catalog" element={<Catalog />} />
 * </Route>
 * ```
 */
export function AdminLayout() {
  const slug = usePortalSlug()
  const auth = useOptionalAdminAuthContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [pendingRequestCount, setPendingRequestCount] = useState(0)

  // Extract auth values with defaults for when context isn't available
  const user = auth?.user ?? null
  const pharmacy = auth?.pharmacy ?? null
  const isAuthenticated = auth?.isAuthenticated ?? false
  const loading = auth?.loading ?? true

  // Fetch pending access request count
  useEffect(() => {
    if (!isAuthenticated || !slug) return

    async function fetchPendingCount() {
      try {
        const result = await listAccessRequests(slug, { status: 'pending' as const, limit: 1 })
        setPendingRequestCount(result.pending_count)
      } catch {
        // Silently fail - badge is non-critical
      }
    }

    fetchPendingCount()
  }, [isAuthenticated, slug])

  // Staff (PORTAL-94) don't see manager/admin-only sections; the backend is
  // authoritative regardless.
  const isStaff = auth?.user?.role === PharmacyUserRole.STAFF
  const navItems = getNavItems(slug)
    .filter((item) => !item.managerOnly || !isStaff)
    .map((item) =>
      item.id === 'access-requests' ? { ...item, badge: pendingRequestCount } : item
    )

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
    },
    [navigate]
  )

  const handleLogout = useCallback(async () => {
    if (auth?.logout) {
      await auth.logout()
    }
    navigate(`/rx/${slug}/admin/login`)
  }, [auth, navigate, slug])

  // If auth context is not available or still loading, show loading
  if (!auth || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
          <p className="text-sm text-charcoal/70">Loading admin portal...</p>
        </div>
      </div>
    )
  }

  // If not authenticated, just render the outlet (will show login page)
  if (!isAuthenticated) {
    return <Outlet />
  }

  return (
    <div className="min-h-screen bg-warm-beige">
      {/* Header */}
      <AdminHeader
        pharmacyName={pharmacy?.name ?? 'Pharmacy'}
        userName={user?.name ?? 'Admin'}
        onLogout={handleLogout}
        onMobileMenuClick={() => setMobileMenuOpen(true)}
      />

      {/* Main content area with sidebar */}
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 bg-white border-r border-gray-200 min-h-[calc(100vh-64px)]">
          <SidebarNav
            items={navItems}
            currentPath={location.pathname}
            onNavigate={handleNavigate}
          />
          <div className="border-t border-gray-200 mx-4" />
          <div className="p-4 pt-2">
            <WhatsNewSidebarItem />
          </div>
        </aside>

        {/* Mobile sidebar */}
        <MobileSidebar
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          items={navItems}
          currentPath={location.pathname}
          onNavigate={handleNavigate}
          pharmacyName={pharmacy?.name ?? 'Pharmacy'}
        />

        {/* Main content */}
        <main className="flex-1 min-h-[calc(100vh-64px)]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
